import { useCallback, useEffect, useRef, useState } from 'react'
import { useAuth } from '@/hooks/useAuth'
import { supabase } from '@/lib/supabase'

/**
 * The paid tier is called **Pro** everywhere a reader can see it. The stored
 * value is still `'premium'`: it is the `user_subscriptions.tier` CHECK
 * constraint, what the Stripe webhook and `redeem-beta-code` write, and what
 * every existing subscriber's row already holds — renaming it would be a data
 * migration, not a rename. Keep the literal, read it as "Pro".
 */
export type SubscriptionTier = 'free' | 'premium'
export type SubscriptionStatus = 'active' | 'canceled' | 'past_due' | 'inactive'

export interface SubscriptionState {
  tier: SubscriptionTier
  status: SubscriptionStatus
  isPro: boolean
  isBetaTester: boolean
  currentPeriodEnd: string | null
  loading: boolean
}

const DEFAULT: SubscriptionState = {
  tier: 'free',
  status: 'inactive',
  isPro: false,
  isBetaTester: false,
  currentPeriodEnd: null,
  loading: false,
}

function isActivePro(tier: string, status: string, periodEnd: string | null): boolean {
  if (tier !== 'premium') return false
  if (status !== 'active') return false
  if (!periodEnd) return true
  return new Date(periodEnd).getTime() > Date.now()
}

export function useSubscription(): SubscriptionState & { refresh: () => void } {
  const { user } = useAuth()
  const userId = user?.id
  // Each hook instance gets its own channel name to avoid the Supabase error
  // "cannot add postgres_changes callbacks after subscribe()" that fires when
  // multiple mounted components share the same channel topic.
  const channelId = useRef(`sub-${Math.random().toString(36).slice(2)}`)
  // Start in loading state — we don't know Pro status until the DB resolves.
  const [state, setState] = useState<SubscriptionState>({ ...DEFAULT, loading: true })
  const [refreshCount, setRefreshCount] = useState(0)

  // Fetch subscription from DB. Runs on mount, userId change, and explicit refresh.
  useEffect(() => {
    if (!userId) {
      setState(DEFAULT)
      return
    }

    let cancelled = false
    setState(prev => ({ ...prev, loading: true }))

    Promise.all([
      supabase
        .from('user_subscriptions')
        .select('tier, status, current_period_end')
        .eq('user_id', userId)
        .maybeSingle(),
      supabase
        .from('beta_code_redemptions')
        .select('id')
        .eq('user_id', userId)
        .maybeSingle(),
    ]).then(([subResult, betaResult]) => {
      if (cancelled) return
      if (subResult.error) {
        console.warn('useSubscription: failed to load subscription:', subResult.error.message)
      }
      if (betaResult.error) {
        console.warn('useSubscription: failed to load beta status:', betaResult.error.message)
      }
      const isBetaTester = !!betaResult.data
      const row = subResult.data ?? null
      if (!row) {
        setState({ ...DEFAULT, isBetaTester, loading: false })
        return
      }
      const tier = (row.tier === 'premium' ? 'premium' : 'free') as SubscriptionTier
      const status = (['active', 'canceled', 'past_due', 'inactive'].includes(row.status)
        ? row.status
        : 'inactive') as SubscriptionStatus
      setState({
        tier,
        status,
        isPro: isActivePro(tier, status, row.current_period_end),
        isBetaTester,
        currentPeriodEnd: row.current_period_end,
        loading: false,
      })
    })

    return () => { cancelled = true }
  }, [userId, refreshCount])

  // Realtime channels — set up once per userId, independent of refresh.
  useEffect(() => {
    if (!userId) return

    const subChannel = supabase
      .channel(`user_subscriptions:${userId}:${channelId.current}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'user_subscriptions', filter: `user_id=eq.${userId}` },
        (payload: { new: Record<string, unknown> | null }) => {
          const row = payload.new as { tier: string; status: string; current_period_end: string | null } | null
          setState(prev => {
            const isBetaTester = prev.isBetaTester
            if (!row) return { ...DEFAULT, isBetaTester, loading: false }
            const tier = (row.tier === 'premium' ? 'premium' : 'free') as SubscriptionTier
            const status = (['active', 'canceled', 'past_due', 'inactive'].includes(row.status as string)
              ? row.status
              : 'inactive') as SubscriptionStatus
            return {
              tier,
              status,
              isPro: isActivePro(tier, status, row.current_period_end),
              isBetaTester,
              currentPeriodEnd: row.current_period_end,
              loading: false,
            }
          })
        },
      )
      .subscribe()

    const betaChannel = supabase
      .channel(`beta_code_redemptions:${userId}:${channelId.current}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'beta_code_redemptions', filter: `user_id=eq.${userId}` },
        (payload: { new: Record<string, unknown> | null }) => {
          const isBetaTester = !!payload.new
          setState(prev => ({ ...prev, isBetaTester }))
        },
      )
      .subscribe()

    return () => {
      supabase.removeChannel(subChannel)
      supabase.removeChannel(betaChannel)
    }
  }, [userId])

  const refresh = useCallback(() => setRefreshCount(c => c + 1), [])

  return { ...state, refresh }
}
